import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import {
  DirectFileUploader,
  FileUploadState,
} from '../../sidebar/DirectFileUploader';
import CloseTaskProofPicker from './CloseTaskProofPicker';
import {
  ChatImage,
  ConfirmationEmailCandidate,
  ConfirmationEmailSearch,
  EMPTY_EMAIL_SEARCH,
  EMPTY_PROOF_SELECTION,
  ProofSelection,
  buildProofPayloads,
  proofImageCandidates,
  proofValidationError,
  tickedChatScreenshotUrls,
} from './closeTaskProof';
import './CloseTaskModal.scss';

interface CloseTaskModalProps {
  show: boolean;
  taskId: string;
  acceptedAt?: string | null;
  /** Task was cancelled by the requester — the submission reports work already performed */
  cancelled?: boolean;
  /** Phone-call tasks stamp "spoke to" proof from the call log */
  isPhoneCall?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function computeDefaultMinutes(acceptedAt?: string | null): number | '' {
  if (!acceptedAt) return '';
  const accepted = new Date(acceptedAt).getTime();
  if (isNaN(accepted)) return '';
  const diffMs = Date.now() - accepted;
  if (diffMs <= 0) return 1;
  return Math.ceil(diffMs / 60000);
}

const CloseTaskModal: React.FC<CloseTaskModalProps> = ({
  show,
  taskId,
  acceptedAt,
  cancelled = false,
  isPhoneCall = false,
  onClose,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [notes, setNotes] = useState('');
  const [timeSpentMinutes, setTimeSpentMinutes] = useState<number | ''>(
    computeDefaultMinutes(acceptedAt),
  );
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [proof, setProof] = useState<ProofSelection>(EMPTY_PROOF_SELECTION);
  const [chatImages, setChatImages] = useState<ChatImage[]>([]);
  const [emailSearch, setEmailSearch] =
    useState<ConfirmationEmailSearch>(EMPTY_EMAIL_SEARCH);
  const [bypassAssistant, setBypassAssistant] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recompute default time each time the modal opens
  useEffect(() => {
    if (show) {
      setTimeSpentMinutes(computeDefaultMinutes(acceptedAt));
    }
  }, [show, acceptedAt]);

  // Screenshots the operator already posted in the task chat are offered as
  // proof so they need not be uploaded a second time.
  useEffect(() => {
    if (!show || !taskId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<{ images?: ChatImage[] }>(
          `/human-tasks/${taskId}/chat-images`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setChatImages(data.images ?? []);
      } catch {
        if (!cancelled) setChatImages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, taskId, getAccessTokenSilently]);

  // Ticking "Confirmation email" runs one bounded check of the requester's
  // inbox; only the emails the matcher ties to this task come back. The
  // lookup runs once per open form (the ref survives re-renders, and is
  // cleared on reset) so flipping the tick off and on does not re-query.
  const emailLookupStarted = useRef(false);
  useEffect(() => {
    if (!show || !taskId || !proof.email.on || emailLookupStarted.current)
      return;
    emailLookupStarted.current = true;
    setEmailSearch({ status: 'loading', candidates: [] });
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<{
          candidates?: ConfirmationEmailCandidate[];
          unavailable?: string;
        }>(`/human-tasks/${taskId}/confirmation-emails`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmailSearch({
          status: 'ready',
          candidates: data.candidates ?? [],
          unavailable: data.unavailable,
        });
      } catch {
        setEmailSearch({ status: 'error', candidates: [] });
      }
    })();
  }, [show, taskId, proof.email.on, getAccessTokenSilently]);

  const proofImages = useMemo(
    () => proofImageCandidates(chatImages, uploadedFiles),
    [chatImages, uploadedFiles],
  );

  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prev) => {
      const existing = prev.findIndex((f) => f.id === fileState.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = fileState;
        return updated;
      }
      return [...prev, fileState];
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setProof((prev) => ({
      ...prev,
      screenshots: {
        ...prev.screenshots,
        keys: prev.screenshots.keys.filter((k) => k !== `file:${fileId}`),
      },
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!notes.trim()) {
      setError('Please provide notes.');
      return;
    }
    if (!timeSpentMinutes || timeSpentMinutes < 1) {
      setError('Time spent must be at least 1 minute.');
      return;
    }

    const pendingUploads = uploadedFiles.some((f) => f.status === 'uploading');
    if (pendingUploads) {
      setError('Please wait for file uploads to finish.');
      return;
    }
    const proofError = proofValidationError(proof, proofImages, emailSearch);
    if (proofError) {
      setError(proofError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const successfulUploads = uploadedFiles.filter(
        (f) => f.status === 'success' && f.url,
      );
      // Screenshots ticked from the task chat ride along as attached images
      // so the assistant review sees exactly what the user will be shown.
      const imageUrls = Array.from(
        new Set([
          ...successfulUploads
            .filter((f) => f.type.startsWith('image/'))
            .map((f) => f.url!),
          ...tickedChatScreenshotUrls(proof, proofImages),
        ]),
      );
      const fileUrls = successfulUploads
        .filter((f) => !f.type.startsWith('image/'))
        .map((f) => f.url!);
      const proofs = buildProofPayloads(
        proof,
        proofImages,
        emailSearch.candidates,
      );

      await api.post(
        `/human-tasks/${taskId}/close`,
        {
          notes: notes.trim(),
          timeSpentMinutes,
          ...(imageUrls.length > 0 && { imageUrls }),
          ...(fileUrls.length > 0 && { fileUrls }),
          ...(proofs && { proofs }),
          bypassAssistant,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onSuccess();
      handleReset();
    } catch (err: any) {
      const msg =
        err.response?.data?.error || 'Failed to close task. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    notes,
    timeSpentMinutes,
    uploadedFiles,
    proof,
    proofImages,
    emailSearch,
    bypassAssistant,
    taskId,
    getAccessTokenSilently,
    onSuccess,
  ]);

  const handleReset = useCallback(() => {
    setNotes('');
    setTimeSpentMinutes(computeDefaultMinutes(acceptedAt));
    setUploadedFiles([]);
    setProof(EMPTY_PROOF_SELECTION);
    setChatImages([]);
    setEmailSearch(EMPTY_EMAIL_SEARCH);
    emailLookupStarted.current = false;
    setBypassAssistant(false);
    setError(null);
  }, [acceptedAt]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [onClose, handleReset]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className='close-task-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {cancelled ? 'Report Work Done' : 'Close Task'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cancelled && (
          <div className='cancelled-report-note'>
            This task was cancelled by the requester. It will stay cancelled —
            your notes below are the only way any work you already did (calls,
            bookings, purchases, commitments) reaches them, so please report it
            faithfully.
          </div>
        )}
        <div className='notes-section'>
          <label className='field-label'>What happened?</label>
          <textarea
            className='notes-input'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='Describe what happened...'
            rows={4}
          />
        </div>

        <div className='time-section'>
          <label className='field-label'>Time spent (minutes)</label>
          <input
            type='number'
            className='time-input'
            value={timeSpentMinutes}
            onChange={(e) => {
              const val = e.target.value;
              setTimeSpentMinutes(
                val === '' ? '' : Math.max(1, parseInt(val, 10) || 1),
              );
            }}
            min={1}
            placeholder='e.g. 15'
          />
        </div>

        <div className='files-section'>
          <label className='field-label'>
            Attachments <span className='optional'>(optional)</span>
          </label>
          {uploadedFiles.length > 0 && (
            <div className='uploaded-files'>
              {uploadedFiles.map((file) => (
                <div key={file.id} className={`uploaded-file ${file.status}`}>
                  {file.previewUrl && (
                    <img
                      src={file.previewUrl}
                      alt={file.name}
                      className='file-preview'
                    />
                  )}
                  <span className='file-name'>{file.name}</span>
                  {file.status === 'uploading' && (
                    <span className='file-status'>Uploading...</span>
                  )}
                  {file.status === 'error' && (
                    <span className='file-status error'>Failed</span>
                  )}
                  <button
                    type='button'
                    className='remove-file-btn'
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <DirectFileUploader
            onFileUpload={handleFileUpload}
            disabled={submitting}
          />
        </div>

        <CloseTaskProofPicker
          value={proof}
          onChange={(next) => {
            setProof(next);
            setError(null);
          }}
          images={proofImages}
          emails={emailSearch}
          isPhoneCall={isPhoneCall}
          disabled={submitting}
        />

        <div className='bypass-section'>
          <label className='bypass-label'>
            <input
              type='checkbox'
              checked={bypassAssistant}
              onChange={(e) => setBypassAssistant(e.target.checked)}
            />
            <span className='bypass-text'>
              Skip secretary review (closes directly, less context forwarded)
            </span>
          </label>
        </div>

        {error && <div className='error-message'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='outline-secondary'
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !notes.trim() || !timeSpentMinutes}
        >
          {submitting
            ? 'Submitting...'
            : cancelled
              ? 'Submit Report'
              : 'Close Task'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseTaskModal;
